import {
  daysHeld,
  Popover,
  type Family,
  type Mode,
  type Plan,
  type PlanStop,
  type TripWeather,
  type WayEnd,
} from '@trippath/shared';
import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { iataOf } from '../../lib/airports';
import { instantOf, minutesSaid, wall, wallOf } from '../../lib/clock';
import { branchesOf, kinOf } from '../family/Family';
import { dayRange, plusDays } from '../../lib/countryFacts';
import { arrivesAt, leavesAt } from '../../lib/gaps';
import { pairs } from '../../lib/map';
import { SPEEDS, useMomentOf, type Moment } from '../../lib/moment';
import { ModeIcon } from '../icons/ModeIcon';
import { SkyIcon } from '../weather/SkyIcon';
import { degrees, hour, isDark, partsOf, skyWord } from '../weather/StopWeather';
import { journeysOf, sunTurns } from '../../lib/sky';
import { SUNRISE, SUNSET } from '../icons/SunIcons';
import { useWidgetBar } from '../widget/Widget';
import { useReadOnly } from '../../app/readOnly';
import {
  codeOf,
  DAY,
  HOUR,
  FEWEST,
  hours,
  layoutOf,
  midnights,
  MOST,
  offsetAt,
  PER_DAY,
  slotAt,
  snapped,
  stepFor,
  zoomed,
  zoomSaid,
  zooms,
  type Block,
  type Way,
  type Zoom,
} from './timeline';

/** The names on the left. */
const NAMES = 120;
/** The rows, top to bottom: the clock picked, by the clock's steps the local one, the way. */
const HEAD = 32;
/** Over the ruler, the strip the line under the pointer says its moment in. */
const PLAY = 18;
/** How near an edge of the view a held stop scrolls it on, and by how much each move. */
const EDGE = 32;
const LOCAL = 32;
const ROUTE = 44;
/** Under the way, room for the mark of a way too short to fill: it hangs under the seam. */
const FOOT = 12;
/** A row of another trip of the family, under the trip's own. */
const KIN = 32;
/** About how wide a character of a way's words is, and what its mark and edges take. */
const LETTER = 7;
const SPARE = 44;
const ZOOM_KEY = 'trippath-timeline-zoom';
/** No day from inside: a timeline of the trip alone, nothing of it to press. */
const NO_AGENDA: readonly AgendaBlock[] = [];
const ignore = () => undefined;
const CLOCK_KEY = 'trippath-timeline-clock';
const locales: Record<string, string> = { et: 'et-EE' };

/** A choice remembered in this browser; the fallback when there is none or it cannot be read. */
export function remembered(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/** Remembers a choice in this browser, when it can. */
export function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // A private window keeps no choice: it holds for this visit only.
  }
}

/** The zoom kept: a day's width, or one of the zooms named; a week's when there is none. */
export function keptZoom(kept: string): number {
  const named = zooms.find((one) => one === kept);
  if (named) return PER_DAY[named];
  const width = Number(kept);
  return width > 0 ? zoomed(width) : PER_DAY.week;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** An offset from UTC said as a clock's: `+4`, `−3:30`. */
export function offsetSaid(ms: number): string {
  const minutes = Math.round(ms / 60_000);
  const whole = Math.abs(minutes);
  const rest = whole % 60;
  return `${minutes < 0 ? '−' : '+'}${String(Math.floor(whole / 60))}${rest ? `:${pad(rest)}` : ''}`;
}

/** What is said of the moment under the pointer, in each clock. */
interface Reading {
  at: number;
  clock: string;
  local: string;
  held: boolean;
}

/** Those drawing the moment under the pointer: told it as it moves, nothing else drawn again. */
const readers = new Set<(reading: Reading | null) => void>();

/** Tells each of them the moment under the pointer; none when it has left. */
function tell(reading: Reading | null) {
  for (const reader of readers) reader(reading);
}

/** Draws what is said of the moment under the pointer, by itself. */
function Read({ render }: { render: (reading: Reading | null) => ReactNode }) {
  const [reading, setReading] = useState<Reading | null>(null);
  useEffect(() => {
    readers.add(setReading);
    return () => {
      readers.delete(setReading);
    };
  }, []);
  return render(reading);
}

/** A change: what it was, said, and how it is taken back. */
interface Done {
  said: string;
  back: () => void;
}

/** A stop being dragged by its end, to stretch it, or by itself, to move it along the way. */
interface Drag {
  kind: 'stretch' | 'move';
  block: Block;
  /** Where the pointer was pressed, and the view's scroll then: the view scrolled while it is
   * held moves it as much as the pointer. */
  x: number;
  scroll: number;
  /** Where the pointer is now. */
  at: number;
  dx: number;
  /** Moved past a press: a drag, not a click. */
  moved: boolean;
}

interface Props {
  plan: Plan;
  /** The stop picked, drawn picked; none when none is. */
  selected: string | null;
  /** The stop whose way on is shown, drawn picked; none when none is. */
  way: string | null;
  /** A stop pressed: it is picked, the map goes to it. */
  onPick: (stop: PlanStop) => void;
  /** Pressed where nothing is: what was picked is let go. */
  onClear?: () => void;
  /** A stop pressed twice: it is opened. */
  onOpen: (stop: PlanStop) => void;
  /** A stop's end stretched, or its days said by the keyboard. */
  onDays: (stop: PlanStop, days: number) => void;
  /** A stop moved from one place in the route to another. */
  onMove: (from: number, to: number) => void;
  /** A stop's way on to leave and arrive at other times: a stop after it later, the days between
   * unplanned. */
  onShift: (stop: PlanStop, departureAt: string | null, arrivalAt: string | null) => void;
  /** A stop to be added after one, into the gap after it. */
  onAddAfter: (stop: PlanStop) => void;
  /** A stop's way on pressed: it is shown, and on the map. */
  onWay: (stop: PlanStop) => void;
  /** The trip's family: each other trip of it a row of its own, at the same time. */
  family?: Family | null;
  /** Another trip's stop pressed: shown on the map, this trip's timeline staying as it is. */
  onKin?: (trip: string, stop: string, way?: boolean) => void;
  /** Another trip's stop picked: marked as this trip's stop picked is. */
  kinPicked?: string | null;
  /** Another trip's way picked, by the stop it leaves: marked as this trip's way picked is. */
  kinWay?: string | null;
  /** Another trip's stop's end stretched, a trip the person may change: its days. */
  onKinDays?: ((trip: string, stop: string, days: number) => void) | undefined;
  /** A stop's day from inside, on its own line under the way: each thing done, each walk. */
  agenda?: readonly AgendaBlock[];
  /** The thing of the day picked, drawn picked; the view goes to it. */
  agendaPicked?: string | null;
  /** A thing of the day pressed. */
  onAgenda?: (key: string) => void;
  /** The day looked at, from and to: the view goes to it as to a stop picked. */
  focus?: readonly [number, number] | null;
  /** Only a stop's days, from and to: the timeline of one place, nothing after it to plan. */
  within?: readonly [number, number] | null;
  /** The trip's weather, a row under the route day by day (the `Weather` mockup); near, each
   * part of the day by its hours. */
  weather?: TripWeather | null;
  /** Another trip's weather, in the weather row while one of its stops is picked. */
  kinWeather?: { trip: string; weather: TripWeather } | null;
  /** A day's weather pressed: its stop is picked, and that day is to be shown in full. */
  onSky?: (stop: string, day: string) => void;
  /** Where its zoom is kept in this browser, and the zoom it starts at: a city's day by the
   * hour, kept apart from the trip's by the week. */
  zoomKey?: string;
  startZoom?: Zoom;
  /** One stop's days alone: its plan, no row of the trip's stops and ways under it. */
  alone?: boolean;
  /** The moment the map shows the sky at: a red line held and drawn along, and the trip played
   * through from it at a speed chosen. */
  moment?: Moment | null;
}

/** A thing of a stop's day on the timeline: what it is, and from when to when. */
export interface AgendaBlock {
  key: string;
  label: string;
  from: number;
  to: number;
  /** A thing done or the way in or on, pressed to pick it; the way to one, by its mark; a room
   * as it is checked into, out of, or got back to. */
  kind: 'thing' | 'move' | 'room';
  /** How a way to a thing goes: `walk`, `metro`, … */
  mode?: string;
  /** What a way to a thing picks when it is pressed: that thing. */
  pick?: string;
  /** A way that cannot be made in time: it would leave before the thing before it ends. */
  late?: boolean;
}

/**
 * The trip in time (the Timeline mockup): the clock picked over it, nearer than a day the local
 * clock of where one is, and the way itself — each stop, each way on, each gap striped red —
 * zoomed as an editor's timeline is, from the whole trip to five minutes. A stop's end is
 * stretched for its days, the stop dragged along the way for its place.
 */
/** A leg of a ticket of more than one: its two ends, when it leaves and arrives, their clocks. */
export interface Leg {
  origin: string;
  destination: string;
  leaves: number;
  arrives: number;
  fromZone: string | null;
  toZone: string | null;
}

/** The legs of a stop's ticket on, when it has more than one and each its times. */
export function legsOf(stop: PlanStop): Leg[] {
  const ticket = stop.bookings.find(
    (booking) =>
      booking.kind === 'ticket' &&
      booking.incident?.kind !== 'cancelled' &&
      booking.legs.length > 1,
  );
  const legs = (ticket?.legs ?? []).flatMap((leg) =>
    leg.departureAt && leg.arrivalAt
      ? [
          {
            origin: leg.origin,
            destination: leg.destination,
            leaves: Date.parse(leg.departureAt),
            arrives: Date.parse(leg.arrivalAt),
            fromZone: leg.originZone,
            toZone: leg.destinationZone,
          },
        ]
      : [],
  );
  return legs.length === ticket?.legs.length ? legs : [];
}

/** A place whose clock the local row reads: a stop, or an end of a way. */
interface Clock {
  id: string;
  city: string;
  zone: string | null;
}

/**
 * Where a change on the way is, at its left: its airport's or station's name where it fits in
 * `room` pixels, else its code (`IST`); nothing when neither does or it has no name.
 */
function airportOf(stop: PlanStop, room: number): ReactNode {
  return airportNamed(stop.place?.name ?? stop.out?.from?.name ?? null, room);
}

/** An airport or station by its name where it fits in `room` pixels, else by its code. */
function airportNamed(name: string | null, room: number): ReactNode {
  if (name === null) return null;
  const code = iataOf(name) ?? name;
  const said = [name, code].find((one) => one.length * LETTER + 8 <= room);
  return said === undefined ? null : <span className="tl-block-name">{said}</span>;
}

/**
 * A ticket of more than one leg, each drawn as long as it takes with its ends and their clocks,
 * the change between two said where it is and how long: RIX → IST, IST 2 h 50 min, IST → PEK.
 */
function legBars(
  legs: readonly Leg[],
  {
    x,
    key,
    mode,
    title,
    picked,
    onPick,
    className,
    style,
    end,
  }: {
    x: (at: number) => number;
    key: string;
    mode: Mode;
    title: string;
    picked: boolean;
    onPick: (() => void) | undefined;
    className: string;
    style: { top?: number; height?: number };
    /** An end by its code and clock, pressed to read the local row by its clock. */
    end: (at: Clock, code: string, time: string | null) => ReactNode;
  },
): ReactNode[] {
  return legs.flatMap((leg, at) => {
    const wide = x(leg.arrives) - x(leg.leaves);
    const leaves = wallOf(new Date(leg.leaves).toISOString(), leg.fromZone).time;
    const arrives = wallOf(new Date(leg.arrives).toISOString(), leg.toZone).time;
    const length = minutesSaid(Math.round((leg.arrives - leg.leaves) / 60_000));
    const fits = (letters: number) => letters * LETTER + SPARE <= wide;
    const ends = fits(leg.origin.length + leg.destination.length + 2);
    const timed = ends && fits(leg.origin.length + leg.destination.length + 14);
    const whole =
      timed && fits(leg.origin.length + leg.destination.length + 17 + length.length);
    const next = legs[at + 1];
    const change = next
      ? (() => {
          const gap = x(next.leaves) - x(leg.arrives);
          const wait = Math.round((next.leaves - leg.arrives) / 60_000);
          const waits = minutesSaid(wait);
          // Drawn as a change on the way is: its mark and its wait, amber; under an hour, red.
          return [
            <span
              key={`${key}-change-${String(at)}`}
              className={`tl-block tl-change${wait < 60 ? ' tl-change-short' : ''} tl-trip-change ${className}`}
              style={{ ...style, left: x(leg.arrives), width: Math.max(6, gap - 2) }}
              title={`${leg.destination} ${waits}`}
            >
              <span className="tl-block-text">
                <span className="tl-change-wait">
                  <ModeIcon mode="change" size={12} />
                  {waits.length * LETTER + 32 <= gap ? waits : ''}
                </span>
              </span>
            </span>,
          ];
        })()
      : [];
    return [
      <span
        key={`${key}-leg-${String(at)}`}
        className={`tl-trip tl-trip-${mode} ${className}${picked ? ' tl-selected' : ''}`}
        style={{ ...style, left: x(leg.leaves), width: wide }}
      >
        {ends &&
          end(
            { id: `${key}-${String(at)}-from`, city: leg.origin, zone: leg.fromZone },
            leg.origin,
            timed ? leaves : null,
          )}
        <button
          type="button"
          className="tl-trip-line"
          aria-label={`${title} · ${leg.origin} → ${leg.destination}`}
          title={`${title} · ${leg.origin} → ${leg.destination}`}
          aria-pressed={picked}
          disabled={!onPick}
          onClick={onPick}
        >
          <ModeIcon mode={mode} size={12} />
          {whole && length}
        </button>
        {ends &&
          end(
            { id: `${key}-${String(at)}-to`, city: leg.destination, zone: leg.toZone },
            leg.destination,
            timed ? arrives : null,
          )}
      </span>,
      ...change,
    ];
  });
}

export function Timeline({
  plan,
  selected,
  way,
  onPick,
  onClear = ignore,
  onOpen,
  onDays,
  onMove,
  onShift,
  onAddAfter,
  onWay,
  family = null,
  onKin,
  kinPicked = null,
  kinWay = null,
  onKinDays,
  agenda = NO_AGENDA,
  agendaPicked = null,
  weather = null,
  kinWeather = null,
  onSky = ignore,
  onAgenda = ignore,
  focus = null,
  within = null,
  zoomKey = ZOOM_KEY,
  startZoom = 'week',
  alone = false,
  moment = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const bar = useWidgetBar();
  const lang = i18n.language;
  const ids = useId();
  const layout = useMemo(() => layoutOf(plan), [plan]);
  // Where each flight sees the sun rise or set on its way, as the plane goes.
  const sunsOnWay = useMemo(
    () =>
      journeysOf(plan)
        .filter((journey) => journey.mode === 'flight')
        .flatMap(sunTurns),
    [plan],
  );
  // The last stop, where the trip's people part with no way on of its own: until the first of
  // its branches leaves, so no hour is drawn without them — or the branch picked.
  const blocks = useMemo(
    () =>
      layout.blocks.map((block, at, all) => {
        if (at < all.length - 1 || block.out !== null) return block;
        const branches = (family ? branchesOf(family, plan.trip.id) : []).filter(
          (trip) => trip.partsAt?.id === block.stop.id,
        );
        // A branch picked: until it leaves; else until the first of them does.
        const picked = branches.filter((trip) =>
          trip.stops.some((stop) => stop.id === kinWay || stop.id === kinPicked),
        );
        const leaves = (picked.length > 0 ? picked : branches)
          .flatMap((trip) => trip.stops.slice(0, 1).flatMap((first) => first.departureAt ?? []))
          .map((departure) => Date.parse(departure));
        const until = Math.min(...leaves);
        return until > block.to && until !== Infinity ? { ...block, to: until } : block;
      }),
    [layout, family, plan.trip.id, kinWay, kinPicked],
  );
  const [perDay, setPerDay] = useState(() => keptZoom(remembered(zoomKey, startZoom)));
  // Bound to a stop's days, from and to; else the trip from its start, room to plan after it.
  const start = within ? within[0] : layout.start;
  const end = within ? within[1] : layout.end;
  const x = (at: number) => ((at - start) / DAY) * perDay;
  const [clock, setClock] = useState(() => remembered(CLOCK_KEY, 'utc'));
  // The place whose clock the local row reads, pressed at a way's end; else where one is.
  const [pin, setPin] = useState<Clock | null>(null);
  // An end of a way by its code and clock: pressed, the local row reads its clock; again, not.
  const clockEnd = (at: Clock, code: string, time: string | null) => {
    const on = pin?.id === at.id;
    return (
      <button
        type="button"
        className="tl-code"
        aria-pressed={on}
        aria-label={t('timeline.clockAt', { city: at.city })}
        onClick={() => {
          setPin(on ? null : at);
        }}
      >
        <b>{code}</b>
        {time && ` ${time}`}
      </button>
    );
  };
  const [drag, setDrag] = useState<Drag | null>(null);
  // Another trip's stop's end held: whose, which, its days, where it was pressed and how far.
  const [kinDrag, setKinDrag] = useState<{
    trip: string;
    stop: string;
    city: string;
    days: number;
    x: number;
    dx: number;
  } | null>(null);
  // A stretch of time being chosen where nothing is, as on an editor's timeline: where it was
  // pressed and where it is held, in the timeline's own pixels.
  const [brush, setBrush] = useState<{
    canvas: Element;
    from: number;
    to: number;
  } | null>(null);
  const brushed = useRef(false);
  const [done, setDone] = useState<Done | null>(null);
  // Why what was asked was not done, said on the bar a while.
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    if (note === null) return;
    const gone = setTimeout(() => {
      setNote(null);
    }, 6000);
    return () => {
      clearTimeout(gone);
    };
  }, [note]);
  const [gapShown, setGapShown] = useState<string | null>(null);
  // Which of its menus is open: the zoom's, the clock's, or none.
  const [menu, setMenu] = useState<'zoom' | 'clock' | null>(null);
  const [nextGap, setNextGap] = useState(0);
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [now] = useState(() => Date.now());
  const today = now > start && now < end;
  // The moment the view is to put `offset` pixels into it — its middle unless said — with a new
  // mark each time: at first today, else where the trip begins, just past the names, so a view
  // zoomed in does not open on the empty half day before it.
  const [aim, setAim] = useState<{ at: number; offset: number | null; n: number } | null>(
    () => {
      if (today) return { at: now, offset: null, n: 0 };
      const first = layout.blocks[0];
      return first && !within ? { at: first.from, offset: NAMES + 48, n: 0 } : null;
    },
  );
  // How many times the view was asked somewhere: each aim a new mark.
  const aims = useRef(0);
  // The zoom last asked for: turns of the wheel faster than drawing go on from it.
  const view = useRef({ perDay });
  // A press that dragged is no click.
  const dragged = useRef(false);
  // What is drawn: the view and a screen either side, so a long trip near stays quick.
  const [shown, setShown] = useState({ left: 0, width: 1600 });
  // Scrolled less than half a screen, what is drawn still covers it: nothing is drawn again.
  const seen = (left: number, width: number) => {
    const wide = Math.max(width, 1600);
    setShown((was) =>
      Math.abs(was.left - left) < was.width / 2 && was.width === wide
        ? was
        : { left, width: wide },
    );
  };
  // The view itself, scrolled by hand: a page that runs past the screen does not move with it.
  const scroller = useRef<HTMLElement>(document.createElement('div'));
  // The view, held once it is there: its width known before it is first scrolled.
  // The mark over what is picked, and what it was last brought into view for.
  const aimed = useRef<HTMLSpanElement>(null);
  const followed = useRef('');
  // Picked here, on the timeline: it is where it is seen, the view not moved for it.
  const here = useRef(false);
  const hold = useCallback((node: HTMLDivElement | null) => {
    setBox(node);
    if (node) {
      scroller.current = node;
      setShown({ left: node.scrollLeft, width: Math.max(node.clientWidth, 1600) });
    }
  }, []);

  // Past the trip's end, time goes on as far as the view is scrolled, as an editor's does: a
  // month at first, and more each time the view comes near the end of what there is.
  const [beyond, setBeyond] = useState(30 * DAY);
  const width = x(within ? end : end + beyond);
  // The clocks to read the timeline in: UTC and each stop's own, by its first city — one a
  // country and offset, Tallinn's and not Tartu's too.
  const zones = new Map<string, string>();
  const kept = new Set<string>();
  for (const stop of plan.stops) {
    const same = `${stop.country} ${String(offsetAt(stop.zone, layout.start))}`;
    if (stop.zone && !kept.has(same)) zones.set(stop.zone, stop.city);
    kept.add(same);
  }
  // A clock kept that this trip has not, home as it once was, reads UTC.
  const clockCity = zones.get(clock);
  const clockZone = clockCity === undefined ? null : clock;
  const step = stepFor(perDay);
  const near = step.kind === 'time';
  // From a day near, the local row: where one is, the date is not the clock's.
  // The local row is always there, the timeline not jumping as it is zoomed: from a day near,
  // each place's own dates; farther, where its clock changes, `UTC+11`.
  const dated = near || step.kind === 'day';
  const holes = blocks.flatMap((block) => (block.hole ? [block.hole] : []));
  const rulerTop = PLAY;
  const localTop = PLAY + HEAD;
  // A stop's day from inside: its plan first, under the clocks, as the day's list walks it.
  const planTop = localTop + LOCAL;
  const routeTop = planTop + (agenda.length > 0 ? KIN : 0);
  // Each other trip of the family a row under the trip's own (the Branches mockup, §3).
  // Only its own branches, as the route shows them.
  const others = family ? branchesOf(family, plan.trip.id) : [];
  // The trip's row, and what is drawn on it: none for one stop's days alone.
  const route = alone ? [] : blocks;
  // A day of a stop of the family begins at its midnight there.
  const midnight = (day: string, zone: string | null) =>
    Date.parse(instantOf(day, '00:00', zone));
  // Each stop of another trip, from and to: one beginning the day the one before it ends begins
  // where that one ends, so no gap between two clocks' midnights is drawn as days unplanned.
  const kinRows = others.map((trip) => ({
    trip,
    spans: trip.stops.reduce<{
      spans: { stop: (typeof trip.stops)[number]; from: number; to: number }[];
      end: number;
      day: string;
      /** When the way to it arrives, said: the stop begins then. */
      arrives: number | null;
    }>(
      (was, stop) => {
        const from =
          was.arrives ?? (was.day === stop.start ? was.end : midnight(stop.start, stop.zone));
        // A way with its times ends the stop when it leaves; it is drawn as long as it takes.
        const way =
          stop.departureAt !== null && stop.arrivalAt !== null
            ? { leaves: Date.parse(stop.departureAt), arrives: Date.parse(stop.arrivalAt) }
            : null;
        const to = Math.max(from, way ? way.leaves : midnight(stop.end, stop.zone));
        return {
          spans: [...was.spans, { stop, from, to }],
          end: to,
          day: stop.end,
          arrives: way ? Math.max(to, way.arrives) : null,
        };
      },
      { spans: [], end: 0, day: '', arrives: null },
    ).spans,
  }));
  const kinSpans = new Map(
    kinRows.flatMap(({ spans }) =>
      spans.map(({ stop, from, to }): [string, [number, number]] => [stop.id, [from, to]]),
    ),
  );
  // Each stop's days by their weather, each from its midnight there and drawn only within the
  // stop's own span — from arrival to leaving — the day before its first and after its last
  // said as those, so no stretch of a stop is without its weather.
  const skyDays = (
    said: TripWeather | null,
    spans: readonly { id: string; zone: string | null; from: number; to: number }[],
  ) =>
    said && said.weather !== 'off'
      ? said.stops.flatMap((one) => {
          const span = spans.find((planned) => planned.id === one.stop);
          // The day before the stop's first said as its first known one, the day after its
          // last as its last known one.
          const known = one.days.filter((day) => day.kind !== null);
          const around = [
            ...known
              .slice(0, 1)
              .flatMap((like) =>
                one.days
                  .slice(0, 1)
                  .map((first) => ({ ...like, day: plusDays(first.day, -1) })),
              ),
            ...one.days,
            ...known
              .slice(-1)
              .flatMap((like) =>
                one.days.slice(-1).map((last) => ({ ...like, day: plusDays(last.day, 1) })),
              ),
          ];
          return span
            ? around.map((day) => ({ day, zone: span.zone, stop: span.id, span }))
            : [];
        })
      : [];
  // The ways whose weather is known as they go: where the plane or the train is, not where it
  // arrives.
  const wayWeather = new Set((weather?.ways ?? []).map((way) => way.stop));
  const weatherDays = skyDays(
    weather,
    // On the way to a stop, its weather already — the weather one arrives into — unless the way
    // has its own.
    blocks.map((block, at, all) => {
      const before = all[at - 1];
      return {
        id: block.stop.id,
        zone: block.stop.zone,
        from:
          before && !wayWeather.has(before.stop.id)
            ? Math.min(block.from, before.to)
            : block.from,
        to: block.to,
      };
    }),
  );
  const kinTop = routeTop + (alone ? 0 : ROUTE + FOOT);
  // Another trip's weather while one of its stops is picked, in the one weather row.
  const kinSky = others.find((trip) => trip.id === kinWeather?.trip);
  const kinSkyDays = skyDays(
    kinWeather?.weather ?? null,
    kinRows
      .filter((row) => row.trip === kinSky)
      .flatMap((row) =>
        row.spans.map(({ stop, from, to }) => ({ id: stop.id, zone: stop.zone, from, to })),
      ),
  );
  const kinWays = kinWeather?.weather.ways ?? [];
  const sky =
    kinSky && kinSkyDays.length > 0
      ? {
          days: kinSkyDays,
          ways: kinWays,
          pick: (stop: string, day: string) => {
            onKin?.(kinSky.id, stop);
            onSky(stop, day);
          },
        }
      : {
          days: weatherDays,
          ways: weather?.ways ?? [],
          pick: (stop: string, day: string) => {
            for (const one of plan.stops.filter((planned) => planned.id === stop)) onPick(one);
            onSky(stop, day);
          },
        };
  // The one weather row, under every trip's.
  const weatherTop = kinTop + others.length * KIN;
  const tall = weatherTop + (sky.days.length > 0 ? KIN : 0);
  const order = plan.countries.map((c) => c.country);
  const tint = (country: string) => `tl-c${String(order.indexOf(country) % 6)}`;
  const days = (count: number) => t('trips.days', { count });
  // A trip read only: no stop stretched, moved or shifted, none added.
  const locked = useReadOnly();
  const editable = (stop: PlanStop) => !locked && !stop.transfer && !daysHeld(plan, stop);
  const wallDay = (at: number, zone: string | null) =>
    new Date(wall(zone ?? 'UTC', at)).toISOString().slice(0, 10);
  const dayMonth = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
  const timeIn = (at: number, zone: string | null) =>
    new Date(wall(zone ?? 'UTC', at)).toISOString().slice(11, 16);
  // A mark of the ruler, as the mockup says it: the hour alone, its minutes only when they step.
  const markIn = (at: number, zone: string | null) =>
    step.kind === 'time' && step.minutes < 60 ? timeIn(at, zone) : timeIn(at, zone).slice(0, 2);
  // A local hour, and under it, from 22 to 6, the night shaded: night is said where it begins.
  const hourMark = (key: string, at: number, zone: string | null) => [
    <span key={key} className="tl-hour" style={{ left: x(at) }}>
      {markIn(at, zone)}
    </span>,
  ];
  // Midnight where a clock is: a cut, the new date said at its line.
  const midnightCut = (key: string, at: number, zone: string | null, said = true) => (
    <span key={key} className="tl-midnight" style={{ left: x(at) }}>
      {said && (
        <span>
          {weekday.format(wall(zone ?? 'UTC', at))} {dayMonth(wallDay(at, zone))}
        </span>
      )}
    </span>
  );
  // A day where a clock is, from its midnight: its date and weekday, `02 L`.
  const dayMark = (key: string, at: number, zone: string | null) => {
    const day = wallDay(at, zone);
    return (
      <span key={key} className="tl-hour tl-local-day" style={{ left: x(at) }}>
        {day.slice(8, 10)} <i>{weekday.format(new Date(`${day}T00:00:00Z`))}</i>
      </span>
    );
  };
  // A stay near, local: its hours on one day, `12:00–18:00`; else each end's date, `10.11 12:00 – 20.11 12:00`.
  const stayed = (from: number, to: number, zone: string | null) =>
    wallDay(from, zone) === wallDay(to, zone)
      ? `${timeIn(from, zone)}–${timeIn(to, zone)}`
      : `${dayMonth(wallDay(from, zone))} ${timeIn(from, zone)} – ${dayMonth(wallDay(to, zone))} ${timeIn(to, zone)}`;
  // Night where one is, 22 to 6, shaded under its hours in one band: no seam between them.
  const nightsIn = (from: number, to: number, zone: string | null) =>
    hours(from, to, zone)
      .filter((at) => {
        const hour = Number(timeIn(at, zone).slice(0, 2));
        return hour >= 22 || hour < 6;
      })
      .reduce<{ start: number; end: number }[]>((runs, at) => {
        const last = runs[runs.length - 1];
        if (last && last.end >= at) {
          last.end = at + HOUR;
          return runs;
        }
        return [...runs, { start: at, end: at + HOUR }];
      }, [])
      .map(({ start, end }) => (
        <span
          key={`n${String(start)}`}
          className="tl-night"
          title={t('timeline.night')}
          style={{ left: x(start), width: Math.min(x(end), x(to)) - x(start) }}
        >
          {/* Night where it begins: a moon, as narrow as an hour can be. */}
          {timeIn(start, zone).startsWith('22') && (
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M20 14.5A8 8 0 1 1 9.5 4 6.5 6.5 0 0 0 20 14.5z" />
            </svg>
          )}
        </span>
      ));
  // Made once a language: making a reader is slow, and each mark of the ruler reads one.
  const { weekday, month } = useMemo(() => {
    const format = (options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat(locales[lang] ?? 'en-GB', { ...options, timeZone: 'UTC' });
    return { weekday: format({ weekday: 'narrow' }), month: format({ month: 'long' }) };
  }, [lang]);
  const clockSaid =
    clockCity === undefined
      ? 'UTC'
      : `${clockCity} UTC${offsetSaid(offsetAt(clock, layout.start))}`;

  // The clock the timeline is read in, kept in this browser.
  const pick = (value: string) => {
    setClock(value);
    remember(CLOCK_KEY, value);
  };
  // The view asked to put a moment `offset` pixels into it, its middle unless said; what is
  // drawn goes there at once, so nothing is blank while the view moves.
  // The middle of the view is that of the days in it, right of the names.
  const put = (at: number, wide: number, offset: number | null) => {
    const el = scroller.current;
    const held = offset ?? (el.clientWidth + NAMES) / 2;
    aims.current += 1;
    setAim({ at, offset: held, n: aims.current });
    const left = ((at - start) / DAY) * wide + NAMES - held;
    seen(Math.max(0, left), el.clientWidth);
  };
  // Once drawn at its width, the view is put where it was asked to be: by its own scroll, read
  // and set where it is — not by bringing a mark into view, which would move a page that runs
  // past the screen too.
  useLayoutEffect(() => {
    if (!aim) return;
    const el = scroller.current;
    el.scrollLeft = Math.max(
      0,
      ((aim.at - start) / DAY) * perDay + NAMES - (aim.offset ?? el.clientWidth / 2),
    );
  }, [aim, perDay, start]);
  // A zoom holds the moment `offset` pixels into the view where it is, as an editor's does: the
  // moment read from the view as it is drawn now, whatever was asked of it since.
  // As far out as it goes: a stop's days filling the view when bound to them, else a year's.
  const leastAt = (width: number) =>
    within ? Math.max(FEWEST, (width - NAMES) / ((end - start) / DAY)) : FEWEST;
  const zoomTo = (next: number, offset: number | null = null) => {
    const el = scroller.current;
    const at =
      start +
      ((el.scrollLeft - NAMES + (offset ?? (el.clientWidth + NAMES) / 2)) / perDay) * DAY;
    const wide = Math.max(leastAt(el.clientWidth), zoomed(next));
    view.current.perDay = wide;
    setPerDay(wide);
    put(at, wide, offset);
    remember(zoomKey, String(wide));
  };
  const look = (at: number) => {
    put(at, perDay, null);
  };
  // A stretch of time in the view, as an editor fits its timeline: `room` of the view either
  // side of it.
  const fit = (from: number, to: number, room: number) => {
    const days = Math.max(to - from, HOUR) / DAY;
    const width = scroller.current.clientWidth;
    const wide = Math.max(leastAt(width), zoomed(((width - NAMES) * (1 - 2 * room)) / days));
    view.current.perDay = wide;
    setPerDay(wide);
    put((from + to) / 2, wide, null);
    remember(zoomKey, String(wide));
  };
  const fitTrip = () => {
    fit(start, end, 0);
  };
  const level = zoomSaid(perDay);
  const clocks: [string, string][] = [['utc', 'UTC'], ...zones];
  // The wheel zooms where the pointer is, smoothly; sideways or with Shift it scrolls.
  useEffect(() => {
    if (!box) return;
    const wheel = (event: WheelEvent) => {
      if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      event.preventDefault();
      // What the line read is gone from under it: it waits for the pointer to move.
      tell(null);
      // Turns come faster than the timeline is drawn: each goes on from the zoom last asked for.
      zoomTo(
        view.current.perDay * Math.exp(-event.deltaY / 700),
        event.clientX - box.getBoundingClientRect().left,
      );
    };
    box.addEventListener('wheel', wheel, { passive: false });
    return () => {
      box.removeEventListener('wheel', wheel);
    };
  });

  const stretched = (d: Drag) => Math.max(0, d.block.stop.days + Math.round(d.dx / perDay));
  // Where a stop dragged along the way would go: the stop under its middle.
  const slotOf = (d: Drag) =>
    slotAt(blocks, (d.block.from + d.block.to) / 2 + (d.dx / perDay) * DAY);
  const changeDays = (stop: PlanStop, next: number) => {
    const was = stop.days;
    if (locked || next === was || next < 0) return;
    onDays(stop, next);
    setDone({
      said: `${stop.city}: ${days(next)}`,
      back: () => {
        onDays(stop, was);
      },
    });
  };
  const moveTo = (stop: PlanStop, from: number, to: number) => {
    if (locked || to === from || to < 0 || to >= plan.stops.length) return;
    onMove(from, to);
    setDone({
      said: t('timeline.moved', { city: stop.city }),
      back: () => {
        onMove(to, from);
      },
    });
  };
  // A stop to another day, whole days on: the way to it leaves as much later, the days between
  // unplanned — a gap, there to plan — or earlier, into such a gap and no further. A way a ticket
  // holds leaves when it does.
  const into = new Map<number, { last: Block; way: Way }>();
  blocks.forEach((one, at) => {
    if (one.out) into.set(at + 1, { last: one, way: one.out });
  });
  const shiftBy = (block: Block, days: number) => {
    const found = into.get(block.at);
    if (locked || !found) return;
    const { last, way } = found;
    // A way a ticket holds leaves when it does: said so.
    if (way.ticketed) {
      setNote(t('timeline.heldByTicket', { from: last.stop.city, to: block.stop.city }));
      return;
    }
    // Its times as they were, chosen how it goes or not yet, to take back.
    const by = Math.max(days, last.hole ? -last.hole.gap.days : 0);
    if (by === 0) return;
    // From when its nights are over, if it leaves before — a night train the evening before.
    const shift = Math.max(way.leave, last.to) + by * DAY - way.leave;
    // As the server reads an instant: to the second, `2026-11-28T05:00:00Z`.
    const at = (instant: number) => `${new Date(instant + shift).toISOString().slice(0, 19)}Z`;
    onShift(last.stop, at(way.leave), way.timed ? at(way.arrive) : null);
    setDone({
      said: t('timeline.shifted', {
        city: block.stop.city,
        date: dayMonth(wallDay(block.from + by * DAY, block.stop.zone)),
      }),
      back: () => {
        onShift(last.stop, leavesAt(last.stop), arrivesAt(last.stop));
      },
    });
  };
  // Let go where it was in the route, it goes to another day; elsewhere, to its place there.
  const finish = (d: Drag) => {
    if (d.kind === 'stretch') changeDays(d.block.stop, stretched(d));
    else if (d.moved && slotOf(d) !== d.block.at) moveTo(d.block.stop, d.block.at, slotOf(d));
    else if (d.moved) shiftBy(d.block, Math.round(d.dx / perDay));
  };
  // A drag follows the pointer anywhere until it is let go.
  useEffect(() => {
    if (!drag) return;
    const view = scroller.current;
    // Where it is held, with as much as the view has been scrolled since it was pressed.
    const follow = (at: number) => {
      const dx = at - drag.x + view.scrollLeft - drag.scroll;
      const moved = drag.moved || Math.abs(dx) > 4;
      dragged.current = moved;
      setDrag({ ...drag, at, dx, moved });
    };
    const move = (event: PointerEvent) => {
      // Held near an edge of the view, the view scrolls on, as an editor's does.
      const box = view.getBoundingClientRect();
      if (box.width > 0 && event.clientX < box.left + NAMES + EDGE) view.scrollLeft -= EDGE;
      else if (box.width > 0 && event.clientX > box.right - EDGE) view.scrollLeft += EDGE;
      follow(event.clientX);
    };
    const scrolled = () => {
      follow(drag.at);
    };
    const up = () => {
      finish(drag);
      setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    view.addEventListener('scroll', scrolled);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      view.removeEventListener('scroll', scrolled);
    };
  });
  // Another trip's stop's end follows the pointer until let go: then its days, to take back.
  // Listened to as soon as it is pressed, before anything is drawn: a quick stretch is not lost.
  useLayoutEffect(() => {
    if (!kinDrag) return;
    const move = (event: PointerEvent) => {
      const dx = event.clientX - kinDrag.x;
      if (Math.abs(dx) > 4) dragged.current = true;
      setKinDrag({ ...kinDrag, dx });
    };
    const up = () => {
      const next = Math.max(0, kinDrag.days + Math.round(kinDrag.dx / perDay));
      setKinDrag(null);
      if (next === kinDrag.days || !onKinDays) return;
      onKinDays(kinDrag.trip, kinDrag.stop, next);
      setDone({
        said: `${kinDrag.city}: ${days(next)}`,
        back: () => {
          onKinDays(kinDrag.trip, kinDrag.stop, kinDrag.days);
        },
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  });
  const target = drag?.kind === 'move' && drag.moved ? blocks[slotOf(drag)] : undefined;
  // One thing picked at a time: the way shown, else the stop picked.
  const picked = way === null ? selected : null;
  // What is picked elsewhere, the way shown first: the timeline goes to it.
  const shownWay = blocks.find((block) => block.stop.id === way)?.out;
  const pickedStop = blocks.find((block) => block.stop.id === selected);
  // The stretch of what is picked: the way shown, else the stop.
  let span: [number, number] | null = null;
  if (pickedStop) span = [pickedStop.from, pickedStop.to];
  if (shownWay) span = [shownWay.leave, shownWay.arrive];
  // Another trip's stop picked: the timeline goes to it as to one of this trip's.
  const kinStop = others.flatMap((trip) => trip.stops).find((stop) => stop.id === kinPicked);
  // Where another trip's stops meet, held to as this trip's own edges are.
  const kinEdges = [...kinSpans.values()].flatMap(([from, to]) => [from, to]);
  // The clock the local row reads: another trip's stop picked, else the place pressed.
  const localAt: { city: string; zone: string | null } | null =
    kinStop && kinStop.zone !== null ? kinStop : pin;
  const kinSpan = kinSpans.get(kinStop?.id ?? '');
  if (kinSpan) span = kinSpan;
  // Another trip's way picked: the seam it goes on from, where its mark is.
  const kinOn = kinSpans.get(kinWay ?? '')?.[1];
  if (kinOn !== undefined) span = [kinOn, kinOn];
  // A day looked at from inside, and a thing of it picked: what is nearest is gone to.
  if (focus) span = [focus[0], focus[1]];
  // A stretch's end held to an edge near it, a stop's, a way's or another trip's, as the moment
  // under the pointer is: from one arrival to a departure exactly.
  const brushAt = (pixel: number) =>
    snapped(blocks, start + (pixel / perDay) * DAY, (12 / perDay) * DAY, kinEdges);
  // The stretch chosen follows the pointer until let go: then it fills the view.
  useEffect(() => {
    if (!brush) return;
    const held = (event: PointerEvent) =>
      event.clientX - brush.canvas.getBoundingClientRect().left;
    const move = (event: PointerEvent) => {
      setBrush({ ...brush, to: held(event) });
    };
    // Where it is let go, not where it was last drawn: it may be let go before that.
    const up = (event: PointerEvent) => {
      const to = held(event);
      setBrush(null);
      if (Math.abs(to - brush.from) <= 4) return;
      brushed.current = true;
      const [one, two] = [brushAt(brush.from), brushAt(to)];
      fit(Math.min(one, two), Math.max(one, two), 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  });
  const thing = agenda.find((block) => (block.pick ?? block.key) === agendaPicked);
  if (thing) span = [thing.from, thing.to];
  const spans = span ? [span] : [];
  // What is picked, on the map, in the list or here, is brought whole into the view, once each
  // time: to its middle, or the view zoomed out to it when it is wider; all in it, it stays.
  useLayoutEffect(() => {
    const key = `${String(selected)} ${String(way)} ${String(kinPicked)} ${String(kinWay)} ${String(agendaPicked)} ${String(focus?.[0])}`;
    if (followed.current === key) return;
    followed.current = key;
    if (here.current) {
      here.current = false;
      return;
    }
    for (const node of aimed.current ? [aimed.current] : []) {
      const view = scroller.current.getBoundingClientRect();
      const aim = node.getBoundingClientRect();
      if (aim.left >= view.left + NAMES && aim.right <= view.right) return;
      if (aim.width > Math.max(view.width - NAMES, 0))
        for (const [from, to] of spans) fit(from, to, 0.1);
      else node.scrollIntoView({ inline: 'center', block: 'nearest' });
    }
  });
  const drawnFrom = start + ((shown.left - shown.width * 1.5) / perDay) * DAY;
  const drawnTo = start + ((shown.left + shown.width * 2.5) / perDay) * DAY;

  // The clock picked: by its steps near, each local midnight a cut with the date; farther, its
  // days, its Mondays or its months.
  const ruler = () => {
    const nights = midnights(drawnFrom, drawnTo, clockZone);
    if (step.kind === 'time') {
      return [
        ...hours(drawnFrom, drawnTo, clockZone, step.minutes).map((at) => (
          <span key={`h${String(at)}`} className="tl-hour" style={{ left: x(at) }}>
            {markIn(at, clockZone)}
          </span>
        )),
        ...nights.map((at) => midnightCut(`m${String(at)}`, at, clockZone)),
      ];
    }
    return nights.flatMap((at, index) => {
      const day = wallDay(at, clockZone);
      const date = new Date(`${day}T00:00:00Z`);
      const first = day.endsWith('-01');
      const dated = step.kind === 'day' || (step.kind === 'week' && date.getUTCDay() === 1);
      if (!(dated || first || index === 0)) return [];
      return [
        <span
          key={at}
          className={`tl-day${first ? ' tl-day-first' : ''}${dated || first ? '' : ' tl-day-bare'}${step.kind === 'day' && date.getUTCDay() % 6 === 0 ? ' tl-weekend' : ''}`}
          style={{ left: x(at), width: step.kind === 'month' ? undefined : perDay }}
        >
          {(first || index === 0) && <span className="tl-month">{month.format(date)}</span>}
          {dated && (
            <span className="tl-date">
              {day.slice(8, 10)}
              {step.kind === 'day' && <i> {weekday.format(date)}</i>}
            </span>
          )}
        </span>,
      ];
    });
  };

  // Near, the clock of where one is — each stop's own, none on the way — or the place pressed.
  // Near, its nights and hours, each midnight cut with the date; a day near, its days.
  const clockIn = (key: string, from: number, to: number, zone: string | null) =>
    step.kind === 'time'
      ? [
          ...nightsIn(from, to, zone),
          ...hours(from, to, zone, step.minutes).flatMap((at) =>
            hourMark(`${key}-${String(at)}`, at, zone),
          ),
          ...midnights(from, to, zone).map((at) =>
            midnightCut(`${key}-m${String(at)}`, at, zone, !clockNights.has(at)),
          ),
        ]
      : midnights(from, to, zone).map((at) =>
          clockNights.has(at) ? (
            <span key={`${key}-d${String(at)}`} className="tl-cut" style={{ left: x(at) }} />
          ) : (
            dayMark(`${key}-d${String(at)}`, at, zone)
          ),
        );
  // Where the clock's midnight is the local one too, its line is drawn, its date not said twice.
  const clockNights = new Set(midnights(drawnFrom, drawnTo, clockZone));
  // Far, where the clock changes from one stop to the next: its offset, at the stop's start.
  // Far, where the clock changes from one stop to the next: its offset, at the stop's start —
  // where it has room after the one before; else the next change that has room says it.
  const zoneMarks = () =>
    blocks.reduce<{ marks: ReactNode[]; said: string; free: number }>(
      (done, block) => {
        const offset = offsetSaid(offsetAt(block.stop.zone, block.from));
        const left = x(block.from);
        if (offset === done.said || left < done.free) return done;
        return {
          marks: [
            ...done.marks,
            <span key={`z${block.stop.id}`} className="tl-hour tl-local-zone" style={{ left }}>
              UTC{offset}
            </span>,
          ],
          said: offset,
          free: left + (3 + offset.length) * LETTER + 8,
        };
      },
      { marks: [], said: '', free: -Infinity },
    ).marks;
  const localClock = () => {
    // A place pressed, or another trip's stop picked, says its clock by its name: far, nothing
    // more to draw.
    if (!dated) return localAt ? [] : zoneMarks();
    if (localAt !== null) return clockIn('pin', drawnFrom, drawnTo, localAt.zone);
    // On the way the clock is where the plane or the train is then, as its weather is known
    // every three hours: Almaty's over Kazakhstan. Where that is not known — out at sea, or no
    // weather — the way is striped, its mark on it, as the mockup has it; unless its two ends
    // keep the same clock: Tartu's runs on to Riga. One stop's days alone know when its ways
    // land: there the clock left with runs on until it does, as one's watch does, and the next
    // stop's starts.
    return blocks.flatMap((block) => {
      const { out } = block;
      const runs = alone || out?.sameClock;
      const until = out && runs ? out.arrive : block.to;
      const from = Math.max(block.from, drawnFrom);
      const to = Math.min(until, drawnTo);
      const striped = (key: string, leave: number, arrive: number, mode: Mode | null) => (
        <span
          key={key}
          className="tl-air"
          style={{ left: x(leave), width: x(arrive) - x(leave) }}
        >
          <ModeIcon mode={mode} size={12} />
        </span>
      );
      const cells = weather?.ways.find((one) => one.stop === block.stop.id)?.cells ?? [];
      const going =
        out && !runs
          ? cells.length > 0
            ? cells.flatMap((cell) => {
                const [leave, arrive] = [Date.parse(cell.from), Date.parse(cell.to)];
                const key = `a${block.stop.id}-${cell.from}`;
                return cell.zone === null
                  ? [striped(key, leave, arrive, out.mode)]
                  : clockIn(
                      key,
                      Math.max(leave, drawnFrom),
                      Math.min(arrive, drawnTo),
                      cell.zone,
                    );
              })
            : [striped(`a${block.stop.id}`, out.leave, out.arrive, out.mode)]
          : [];
      return [...clockIn(block.stop.id, from, to, block.stop.zone), ...going];
    });
  };

  // The moment under the pointer, in the clock picked and where one is then, or at the place
  // pressed; held to an edge of a stop or a way near it, as an editor's playhead is.
  const clockAt = (at: number) =>
    `${weekday.format(wall(clockZone ?? 'UTC', at))} ${dayMonth(wallDay(at, clockZone))} ${timeIn(at, clockZone)}`;
  // A stretch being chosen, from where its ends are held: its ends in the clock picked, and how
  // long it is.
  const brushDrawn = (one: number, two: number) => {
    const [from, to] = [Math.min(one, two), Math.max(one, two)];
    const days = (to - from) / DAY;
    return (
      <div
        className="tl-brush"
        aria-hidden="true"
        style={{ left: x(from), width: x(to) - x(from) }}
      >
        <span className="tl-read">
          {clockAt(from)} – {clockAt(to)} ·{' '}
          {days < 2
            ? minutesSaid(Math.round(days * 1440))
            : t('trips.days', { count: Math.round(days) })}
        </span>
      </div>
    );
  };
  // The time where one is at a minute: at the stop then, its own clock; between two stops on the
  // way; before the trip or after it, nowhere.
  const localOf = (minute: number) => {
    const where =
      localAt ?? blocks.find((block) => minute >= block.from && minute < block.to)?.stop;
    return where
      ? `${timeIn(minute, where.zone)} ${where.city}`
      : minute >
            layout.blocks.reduce((first, block) => Math.min(first, block.from), Infinity) &&
          minute < layout.blocks.reduce((last, block) => Math.max(last, block.to), -Infinity)
        ? t('timeline.inAir')
        : '';
  };
  const reading = (at: number): Reading => {
    const held = snapped(blocks, at, (8 / perDay) * DAY, kinEdges);
    const minute = Math.round(held / 60_000) * 60_000;
    return { at: minute, held: held !== at, clock: clockAt(minute), local: localOf(minute) };
  };
  // Pressed on the clocks over the rows, the map's moment is there — held to an edge near it, as
  // the line under the pointer is — what is picked kept.
  const seek = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!moment) return;
    event.stopPropagation();
    const left = event.currentTarget.getBoundingClientRect().left;
    moment.pause();
    moment.set(reading(start + ((event.clientX - left) / perDay) * DAY).at);
  };
  // A moment said as the line under the pointer says it: in the clock picked, and where one is.
  const momentSaid = (at: number) => {
    const minute = Math.round(at / 60_000) * 60_000;
    const local = localOf(minute);
    return local === '' ? clockAt(minute) : `${clockAt(minute)} · ${local}`;
  };

  const stopBlock = (block: Block) => {
    const { stop } = block;
    const stretching = drag?.kind === 'stretch' && drag.block === block;
    const length = stretching ? stretched(drag) : stop.days;
    const left = x(block.from);
    const right = x(block.to) + (length - stop.days) * perDay;
    const lifted = drag?.kind === 'move' && drag.block === block && drag.moved;
    const booked = Math.max(0, stop.days - stop.openNights) / Math.max(1, stop.days);
    const wait = Math.round((block.to - block.from) / 60_000);
    // A change on the way is its wait, amber; under an hour, red.
    const kind = stop.transfer
      ? `tl-change${wait < 60 ? ' tl-change-short' : ''}`
      : tint(stop.country);
    return (
      <button
        key={stop.id}
        type="button"
        className={`tl-block ${kind}${lifted ? ' tl-lifted' : ''}${picked === stop.id ? ' tl-selected' : ''}`}
        style={{
          left,
          width: Math.max(6, right - left - 2),
          top: routeTop + 6,
          height: ROUTE - 12,
          transform: lifted ? `translateX(${String(drag.dx)}px)` : undefined,
        }}
        aria-label={t('timeline.block', {
          city: stop.city,
          from: dayMonth(stop.start),
          to: dayMonth(stop.end),
          days: days(stop.days),
        })}
        aria-pressed={picked === stop.id}
        aria-describedby={`${ids}-keys`}
        onPointerDown={(event) => {
          dragged.current = false;
          if (locked) return;
          setDrag({
            kind: 'move',
            block,
            x: event.clientX,
            scroll: scroller.current.scrollLeft,
            at: event.clientX,
            dx: 0,
            moved: false,
          });
        }}
        onClick={() => {
          if (dragged.current) return;
          // Its clock read on the local row; pressed again, where one is.
          setPin(pin?.id === stop.id ? null : stop);
          here.current = picked !== stop.id;
          onPick(stop);
        }}
        // Pressed twice, it fills the view and is opened.
        onDoubleClick={() => {
          fit(block.from, block.to, 0.1);
          onOpen(stop);
        }}
        onKeyDown={(event) => {
          const before = event.key === 'ArrowLeft';
          if (!before && event.key !== 'ArrowRight') return;
          event.preventDefault();
          if (event.altKey) moveTo(stop, block.at, block.at + (before ? -1 : 1));
          else if (event.shiftKey) shiftBy(block, before ? -1 : 1);
          else if (editable(stop)) changeDays(stop, stop.days + (before ? -1 : 1));
        }}
      >
        <span className="tl-block-text">
          {/* A change on the way is its wait alone: its place is on the ways either side. */}
          {!stop.transfer && <span className="tl-block-name">{stop.city}</span>}
          {/* A change on the way: its airport or station, then its mark and its wait where they
              fit. */}
          {stop.transfer &&
            airportOf(stop, right - left - 32 - minutesSaid(wait).length * LETTER)}
          {stop.transfer && (
            <span className="tl-change-wait">
              <ModeIcon mode="change" size={12} />
              {minutesSaid(wait).length * LETTER + 32 <= right - left && minutesSaid(wait)}
            </span>
          )}
          <span className="tl-block-days">
            {!stop.transfer && near && stayed(block.from, block.to, stop.zone)}
            {!stop.transfer &&
              !near &&
              (stretching ? `${String(stop.days)} → ${days(length)}` : days(stop.days))}
          </span>
        </span>
        {!stop.transfer && (
          <span className="tl-booked" style={{ width: `${String(booked * 100)}%` }} />
        )}
        {editable(stop) && (
          <span
            className="tl-grip"
            aria-hidden="true"
            onPointerDown={(event) => {
              event.stopPropagation();
              dragged.current = false;
              setDrag({
                kind: 'stretch',
                block,
                x: event.clientX,
                scroll: scroller.current.scrollLeft,
                at: event.clientX,
                dx: 0,
                moved: false,
              });
            }}
          />
        )}
      </button>
    );
  };

  // The way on from a stop to the next: filled as long as it takes, its mark; where its words
  // fit, its ends with their local times, +1 and its length.
  const wayBlock = (block: Block, out: Way, next: Block) => {
    const wide = x(out.arrive) - x(out.leave);
    // A change on the way is a stop of its own: each leg then drawn as its own way.
    const legs = next.stop.transfer ? [] : legsOf(block.stop);
    if (legs.length > 1 && wide >= 24 * legs.length && out.mode !== null) {
      const said = t(`mode.${out.mode}`);
      return (
        <Fragment key={`w${block.stop.id}`}>
          {legBars(legs, {
            x,
            key: `w${block.stop.id}`,
            mode: out.mode,
            title: `${said}: ${block.stop.city} → ${next.stop.city}`,
            picked: way === block.stop.id,
            onPick: () => {
              here.current = way !== block.stop.id;
              onWay(block.stop);
            },
            className: '',
            style: { top: routeTop + 6, height: ROUTE - 12 },
            end: clockEnd,
          })}
        </Fragment>
      );
    }
    const said = out.mode ? t(`mode.${out.mode}`) : t('calendar.travel');
    const title = `${said}: ${block.stop.city} → ${next.stop.city}`;
    const leaves = wallOf(new Date(out.leave).toISOString(), block.stop.zone);
    const arrives = wallOf(new Date(out.arrive).toISOString(), next.stop.zone);
    const later = Math.round((Date.parse(arrives.day) - Date.parse(leaves.day)) / DAY);
    const shownNow = way === block.stop.id;
    // Each end by its airport's code, else its city: pressed, the local row reads its clock.
    const end = (stop: PlanStop, at: WayEnd | null, time: string | null) =>
      clockEnd(stop, codeOf(at, stop.city), time);
    const show = () => {
      here.current = way !== block.stop.id;
      onWay(block.stop);
    };
    // Too short to draw, a way is its mark on the seam between the two stops, at their foot.
    if (wide < 24) {
      // A stop of no time after it, left at once by a way drawn long: the mark under the seam
      // would be under that way's start, as if a second of it. It hangs before the seam instead.
      const onward = next.out ? x(next.out.arrive) - x(next.out.leave) : 0;
      const starts = next.out ? Math.min(x(out.arrive), x(next.out.leave)) : x(out.arrive);
      const middle = (x(out.leave) + x(out.arrive)) / 2;
      const left = next.from === next.to && onward >= 24 ? starts - 26 : middle - 12;
      return (
        <button
          key={`w${block.stop.id}`}
          type="button"
          className={`tl-trip tl-trip-seam${shownNow ? ' tl-selected' : ''}`}
          style={{ left, top: routeTop + ROUTE - 12 }}
          aria-label={title}
          aria-pressed={shownNow}
          onClick={show}
        >
          <ModeIcon mode={out.mode} size={12} />
        </button>
      );
    }
    // Its words by what fits: ends with times and its length, then ends with times, then its
    // ends' codes, then its mark alone.
    const length = minutesSaid(Math.round((out.arrive - out.leave) / 60_000));
    const extra = out.timed ? (later > 0 ? `+${String(later)} · ${length}` : length) : '';
    const from = codeOf(out.from, block.stop.city);
    const to = codeOf(out.to, next.stop.city);
    const times = out.timed ? leaves.time.length + arrives.time.length + 2 : 0;
    const fits = (letters: number) => letters * LETTER + SPARE <= wide;
    const ends = fits(from.length + to.length + 2);
    const timed = ends && fits(from.length + to.length + 2 + times);
    const whole = timed && fits(from.length + to.length + 2 + times + extra.length + 3);
    return (
      <span
        key={`w${block.stop.id}`}
        className={`tl-trip tl-trip-${out.mode ?? 'travel'}${shownNow ? ' tl-selected' : ''}`}
        style={{ left: x(out.leave), width: wide, top: routeTop + 6, height: ROUTE - 12 }}
      >
        {ends && end(block.stop, out.from, timed && out.timed ? leaves.time : null)}
        <button
          type="button"
          className="tl-trip-line"
          aria-label={title}
          aria-pressed={shownNow}
          onClick={show}
        >
          <ModeIcon mode={out.mode} size={12} />
          {whole && extra}
        </button>
        {ends && end(next.stop, out.to, timed && out.timed ? arrives.time : null)}
      </span>
    );
  };

  const tools = (
    <div className="tl-tools">
      {note !== null && (
        <p className="tl-note" role="status">
          {note}
        </p>
      )}
      {/* The last change, taken back: an editor's undo, beside the rest. */}
      {done && (
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={`${t('plan.undo')}: ${done.said}`}
          title={`${t('plan.undo')}: ${done.said}`}
          onClick={() => {
            done.back();
            setDone(null);
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3" />
          </svg>
        </button>
      )}
      {holes.length > 0 && (
        <button
          type="button"
          className="tl-gaps"
          onClick={() => {
            const at = nextGap % holes.length;
            for (const hole of holes.slice(at, at + 1)) look(hole.from);
            setNextGap(nextGap + 1);
          }}
        >
          {t('timeline.gaps', { count: holes.length })}
        </button>
      )}
      {moment && (
        // The trip played through on the map: its nights going by, each flight flown.
        <PlayControls moment={moment} />
      )}
      <div className="tl-zoom" role="group" aria-label={t('timeline.zoom')}>
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('timeline.zoomOut')}
          title={t('timeline.zoomOut')}
          disabled={perDay <= leastAt(box?.clientWidth ?? 0)}
          onClick={() => {
            zoomTo(perDay / 2);
          }}
        >
          −
        </button>
        {/* The zoom as an editor says it: the nearest named, and how many times nearer. */}
        <Popover
          open={menu === 'zoom'}
          onOpenChange={(open) => {
            setMenu(open ? 'zoom' : null);
          }}
          label={t('timeline.zoom')}
          trigger={(props) => (
            <button type="button" className="tl-menu-button" {...props}>
              {t(`timeline.zooms.${level.zoom}`)}
              {level.times > 1 && ` ×${String(level.times)}`} ▾
            </button>
          )}
        >
          <div className="tl-menu">
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                fitTrip();
              }}
            >
              {t('timeline.fit')}
            </button>
            {zooms.map((one) => (
              <button
                key={one}
                type="button"
                aria-pressed={perDay === PER_DAY[one]}
                onClick={() => {
                  setMenu(null);
                  zoomTo(PER_DAY[one]);
                }}
              >
                {t(`timeline.zooms.${one}`)}
              </button>
            ))}
          </div>
        </Popover>
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('timeline.zoomIn')}
          title={t('timeline.zoomIn')}
          disabled={perDay >= MOST}
          onClick={() => {
            zoomTo(perDay * 2);
          }}
        >
          +
        </button>
        {/* The whole trip in the view: its ends drawn apart. */}
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('timeline.fit')}
          title={t('timeline.fit')}
          onClick={fitTrip}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 5v14M21 5v14M7 12h10M10 9l-3 3 3 3M14 9l3 3-3 3" />
          </svg>
        </button>
        {/* What is picked in the view: framed. */}
        <button
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('timeline.fitPicked')}
          title={t('timeline.fitPicked')}
          disabled={spans.length === 0}
          onClick={() => {
            for (const [from, to] of spans) fit(from, to, 0.1);
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />
            <rect x="9" y="9" width="6" height="6" rx="1" />
          </svg>
        </button>
      </div>
    </div>
  );
  // A row of weather: each day by its sky and high, or once its shortest part has room for its
  // sky and temperature, each part by its hours, the dark ones by the moon; one pressed picks its
  // stop.
  const parted = (perDay * 3) / 24 >= 48;
  // A stretch narrower than this has no room for its sky and temperature: a stop's days side by
  // side are said together, by their mean.
  const ROOM = 44;
  const skyRow = (
    days: ReturnType<typeof skyDays>,
    ways: TripWeather['ways'],
    top: number,
    label: string,
    onDay: (stop: string, day: string) => void,
  ) => {
    // On the way: the weather where it is, every three hours, by the clock read.
    const going = ways.flatMap(({ stop, cells: along }) =>
      along.map((cell) => {
        const from = Date.parse(cell.from);
        const to = Date.parse(cell.to);
        return {
          key: `way ${cell.from}`,
          night: cell.night,
          from,
          to,
          sky: cell.sky,
          temp: cell.temp,
          rain: cell.rainChance,
          said: t('weather.onWay', {
            from: timeIn(from, clockZone),
            to: timeIn(to, clockZone),
            sky: skyWord(t, cell.sky, cell.night),
            temp: degrees(cell.temp),
          }),
          stop,
          day: cell.day,
          forecast: true,
        };
      }),
    );
    const cells = days.flatMap(({ day, zone, stop, span }) => {
      if (day.kind === null) return [];
      const drawn =
        parted && day.parts.length > 0
          ? partsOf(day.parts).map(({ name, from, to, part }) => ({
              key: name,
              night: isDark(from, to, day.sunrise, day.sunset),
              from: Date.parse(instantOf(day.day, `${hour(from)}:00`, zone)),
              to: Date.parse(
                instantOf(
                  to === 24 ? plusDays(day.day, 1) : day.day,
                  `${hour(to % 24)}:00`,
                  zone,
                ),
              ),
              sky: part.sky,
              temp: part.temp,
              rain: part.rainChance,
              said: t('weather.partSaid', {
                part: t(`weather.part.${name}`),
                from: hour(from),
                to: hour(to),
                sky: skyWord(t, part.sky, isDark(from, to, day.sunrise, day.sunset)),
                temp: degrees(part.temp),
              }),
            }))
          : [
              {
                key: 'day',
                night: false,
                from: midnight(day.day, zone),
                to: midnight(plusDays(day.day, 1), zone),
                sky: day.sky,
                temp: day.high,
                rain: day.rainChance,
                said: `${day.day} · ${t(`weather.sky.${day.sky}`)} · ${degrees(day.high)} / ${degrees(day.low)}`,
              },
            ];
      // Only within the stop's own span.
      return drawn.flatMap((cell) => {
        const from = Math.max(cell.from, span.from);
        const to = Math.min(cell.to, span.to);
        return to > from
          ? [{ ...cell, from, to, stop, day: day.day, forecast: day.kind !== 'usual' }]
          : [];
      });
    });
    // Too narrow, a stop's days side by side are put together until they have room.
    type Cell = (typeof cells)[number];
    const groups = [...cells, ...going]
      .sort((one, other) => one.from - other.from)
      .reduce<{ head: Cell; cells: Cell[] }[]>((made, cell) => {
        const last = made.at(-1);
        if (
          last !== undefined &&
          last.head.stop === cell.stop &&
          perDay < ROOM &&
          // Room still to fill, or less than a whole day — one left on — no stretch alone.
          (x(cell.from) - x(last.head.from) < ROOM || x(cell.to) - x(cell.from) < perDay - 1)
        ) {
          last.cells.push(cell);
        } else {
          made.push({ head: cell, cells: [cell] });
        }
        return made;
      }, []);
    const shown = groups.map(({ head, cells: group }) => {
      if (group.length === 1) return head;
      const end = group.reduce((_, one) => one, head);
      const skies = group.map((one) => one.sky);
      const sky = skies.reduce((best, one) =>
        skies.filter((seen) => seen === one).length >
        skies.filter((seen) => seen === best).length
          ? one
          : best,
      );
      const temp = group.reduce((sum, one) => sum + one.temp, 0) / group.length;
      return {
        ...head,
        to: end.to,
        sky,
        temp,
        rain: null,
        said: t('weather.average', {
          from: dayMonth(head.day),
          to: dayMonth(end.day),
          sky: t(`weather.sky.${sky}`),
          temp: degrees(temp),
        }),
      };
    });
    return (
      <ul className="tl-weather" style={{ top, height: KIN }} aria-label={label}>
        {shown.map((cell) => {
          const wide = x(cell.to) - x(cell.from);
          return (
            <li
              key={`${cell.stop} ${cell.day} ${cell.key}`}
              className={cell.forecast ? 'tl-sky tl-sky-forecast' : 'tl-sky'}
              data-part={cell.key}
              data-dark={String(cell.night)}
              style={{ left: x(cell.from), width: wide }}
            >
              <button
                type="button"
                title={cell.said}
                aria-label={cell.said}
                onClick={() => {
                  onDay(cell.stop, cell.day);
                }}
              >
                {/* As much as there is room for: the sky, its temperature, its rain. */}
                {wide >= 18 && <SkyIcon sky={cell.sky} size={16} night={cell.night} />}
                {wide >= 40 && <span>{degrees(cell.temp)}</span>}
                {cell.rain !== null && wide >= 80 && (
                  <span className="weather-rain">{String(cell.rain)} %</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <section className="panel timeline" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`} className="visually-hidden">
        {t('timeline.title')}
      </h2>
      {/* Its tools on its widget's bar, where there is one: no row of its own. */}
      {bar ? createPortal(tools, bar) : tools}
      <p id={`${ids}-keys`} className="visually-hidden">
        {t('timeline.keys')}
      </p>
      <div
        className="tl-scroll"
        ref={hold}
        role="group"
        aria-label={t('timeline.title')}
        onScroll={(event) => {
          const el = event.currentTarget;
          seen(el.scrollLeft, el.clientWidth);
          if (el.scrollLeft + 2 * el.clientWidth >= el.scrollWidth) {
            setBeyond((was) => was + Math.max(30 * DAY, ((2 * el.clientWidth) / perDay) * DAY));
          }
        }}
        onKeyDown={(event) => {
          if (event.key === '+' || event.key === '=') zoomTo(perDay * 2);
          if (event.key === '-') zoomTo(perDay / 2);
        }}
      >
        <div className="tl-grid" style={{ width: NAMES + width }}>
          <div className="tl-names" style={{ width: NAMES }}>
            {/* The clock the timeline is read in, picked where it is said. */}
            <div className="tl-name tl-name-clock" style={{ height: PLAY + HEAD }}>
              <Popover
                open={menu === 'clock'}
                onOpenChange={(open) => {
                  setMenu(open ? 'clock' : null);
                }}
                label={t('timeline.clock')}
                trigger={(props) => (
                  <button
                    type="button"
                    className="tl-menu-button"
                    {...props}
                    aria-label={`${t('timeline.clock')}: ${clockSaid}`}
                  >
                    {clockSaid} ▾
                  </button>
                )}
              >
                <div className="tl-menu">
                  {clocks.map(([value, said]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={(clockZone ?? 'utc') === value}
                      onClick={() => {
                        setMenu(null);
                        pick(value);
                      }}
                    >
                      {said}
                    </button>
                  ))}
                </div>
              </Popover>
            </div>
            <div className="tl-name" style={{ height: LOCAL }}>
              {localAt
                ? `${localAt.city} UTC${offsetSaid(offsetAt(localAt.zone, kinStop ? midnight(kinStop.start, localAt.zone) : layout.start))}`
                : t('timeline.local')}
            </div>
            {agenda.length > 0 && (
              <div className="tl-name" style={{ height: KIN }}>
                {t('city.things')}
              </div>
            )}
            {!alone && (
              <div className="tl-name" style={{ height: ROUTE + FOOT }}>
                {family && others.length > 0 && (
                  <span
                    className={`tl-kin-name kin-${String(kinOf(family, plan.trip.id).kin)}`}
                  >
                    {plan.trip.name}
                  </span>
                )}
              </div>
            )}
            {family &&
              others.map((trip) => (
                <div key={trip.id} className="tl-name" style={{ height: KIN }}>
                  <span className={`tl-kin-name kin-${String(kinOf(family, trip.id).kin)}`}>
                    <span className="kin-dot" aria-hidden="true" /> {trip.name}
                  </span>
                </div>
              ))}
            {sky.days.length > 0 && (
              <div className="tl-name" style={{ height: KIN }}>
                {t('weather.lane')}
              </div>
            )}
          </div>
          <div
            className={within ? 'tl-canvas tl-bounded' : 'tl-canvas'}
            style={{ width, height: tall }}
            onPointerDown={(event) => {
              // Held and drawn across, a stretch of time is chosen to zoom to: from anywhere —
              // a day's weather, a way, another trip's stop — but a stop of this trip, which is
              // moved by it, and its end, which stretches it.
              if (
                event.button !== 0 ||
                (event.target as Element).closest('.tl-block, .tl-grip, .tl-moment, a, input')
              )
                return;
              event.preventDefault();
              const canvas = event.currentTarget;
              const at = event.clientX - canvas.getBoundingClientRect().left;
              setBrush({ canvas, from: at, to: at });
            }}
            onClickCapture={(event) => {
              // A stretch chosen is no press, of what it was begun on either.
              if (brushed.current) {
                brushed.current = false;
                event.stopPropagation();
              }
            }}
            onClick={(event) => {
              // Pressed where nothing is, as on an editor's timeline: what was picked is let go.
              if (!(event.target as Element).closest('button, a, input')) onClear();
            }}
            onPointerMove={(event) => {
              const left = event.currentTarget.getBoundingClientRect().left;
              tell(
                drag || brush ? null : reading(start + ((event.clientX - left) / perDay) * DAY),
              );
            }}
            onPointerLeave={() => {
              tell(null);
            }}
          >
            {brush && brushDrawn(brushAt(brush.from), brushAt(brush.to))}
            <div
              className={moment ? 'tl-ruler tl-seek' : 'tl-ruler'}
              style={{ top: rulerTop, height: HEAD }}
              onClick={seek}
            >
              {ruler()}
            </div>
            <div
              className={moment ? 'tl-local tl-seek' : 'tl-local'}
              style={{ top: localTop, height: LOCAL }}
              onClick={seek}
            >
              {localClock()}
            </div>
            {!alone && <div className="tl-lane" style={{ top: routeTop, height: ROUTE }} />}
            {sky.days.length > 0 &&
              skyRow(sky.days, sky.ways, weatherTop, t('weather.lane'), sky.pick)}
            {/* The stop's day from inside: each thing done, pressed to pick it, and the way to it
                before it by its mark and how long. */}
            {agenda.length > 0 && (
              <div className="tl-agenda" style={{ top: planTop, height: KIN }}>
                {agenda.map((block) => {
                  const place = {
                    left: x(block.from),
                    width: Math.max(4, x(block.to) - x(block.from)),
                  };
                  if (block.kind === 'move') {
                    // Pressed, the way is picked: the way shown on the map, said beside it.
                    const late = block.late ? ' tl-late' : '';
                    const on = agendaPicked === (block.pick ?? block.key) ? ' tl-selected' : '';
                    return (
                      <button
                        key={block.key}
                        type="button"
                        className={`tl-agenda-move${late}${on}`}
                        aria-label={block.label}
                        title={block.label}
                        style={place}
                        onClick={() => {
                          here.current = true;
                          onAgenda(block.pick ?? block.key);
                        }}
                      >
                        <ModeIcon mode={block.mode} size={11} />
                        {/* Too narrow for its time: its mark alone, the time in its title. */}
                        {place.width >= 52 && <span>{block.label}</span>}
                      </button>
                    );
                  }
                  const picks = block.pick ?? block.key;
                  const on = agendaPicked === picks;
                  const room = block.kind === 'room' ? ' tl-agenda-room' : '';
                  return (
                    <button
                      key={block.key}
                      type="button"
                      className={`tl-agenda-thing${room}${on ? ' tl-selected' : ''}`}
                      aria-pressed={on}
                      aria-label={block.label}
                      title={block.label}
                      style={place}
                      onClick={() => {
                        here.current = true;
                        onAgenda(picks);
                      }}
                    >
                      {block.label}
                    </button>
                  );
                })}
              </div>
            )}
            {family &&
              kinRows.map(({ trip, spans }, row) => (
                <div
                  key={trip.id}
                  className={`tl-kin kin-${String(kinOf(family, trip.id).kin)}`}
                  style={{ top: kinTop + row * KIN, height: KIN }}
                >
                  {/* Its setting out, a stop of no days in the town it parts at, is no block. */}
                  {spans
                    .filter(
                      ({ stop }, at) =>
                        !(at === 0 && stop.days === 0 && stop.city === trip.partsAt?.city),
                    )
                    .map(({ stop, from: start, to: end }) => {
                      const from = x(start);
                      const said = `${stop.city} · ${t('trips.days', { count: stop.days })}`;
                      const held = kinDrag?.stop === stop.id ? kinDrag.dx : 0;
                      const place = {
                        left: from,
                        width: Math.max(4, x(end) - from + held),
                      };
                      // A change on the way, as this trip's own: its airport, its mark and wait.
                      const wait = Math.round((end - start) / 60_000);
                      const change = stop.transfer
                        ? ` tl-block tl-change${wait < 60 ? ' tl-change-short' : ''}`
                        : '';
                      const named = stop.transfer ? (
                        <span className="tl-block-text">
                          {airportNamed(
                            stop.ends?.from ?? null,
                            place.width - 32 - minutesSaid(wait).length * LETTER,
                          )}
                          <span className="tl-change-wait">
                            <ModeIcon mode="change" size={12} />
                            {minutesSaid(wait).length * LETTER + 32 <= place.width &&
                              minutesSaid(wait)}
                          </span>
                        </span>
                      ) : (
                        stop.city
                      );
                      return onKin ? (
                        <button
                          key={stop.id}
                          type="button"
                          className={`tl-kin-stop${change}${kinPicked === stop.id ? ' tl-selected' : ''}`}
                          aria-pressed={kinPicked === stop.id}
                          aria-label={t('family.open', { name: trip.name, stop: said })}
                          title={t('family.open', { name: trip.name, stop: said })}
                          style={place}
                          onClick={() => {
                            // A stretch let go is no press.
                            if (dragged.current) {
                              dragged.current = false;
                              return;
                            }
                            // Pressed once it is picked where it is; twice, it fills the view.
                            here.current = kinPicked !== stop.id;
                            onKin(trip.id, stop.id);
                          }}
                          onDoubleClick={() => {
                            fit(start, end, 0.1);
                          }}
                        >
                          {named}
                          {/* Its end stretched, as this trip's own: its days. */}
                          {onKinDays && trip.role !== null && trip.role !== 'viewer' && (
                            <span
                              className="tl-grip"
                              aria-hidden="true"
                              onPointerDown={(event) => {
                                event.stopPropagation();
                                dragged.current = false;
                                setKinDrag({
                                  trip: trip.id,
                                  stop: stop.id,
                                  city: stop.city,
                                  days: stop.days,
                                  x: event.clientX,
                                  dx: 0,
                                });
                              }}
                            />
                          )}
                        </button>
                      ) : (
                        <span
                          key={stop.id}
                          className={`tl-kin-stop${change}`}
                          title={said}
                          style={place}
                        >
                          {named}
                        </span>
                      );
                    })}
                  {/* How it goes on from each stop, on the seam, as this trip's own ways are;
                      one cancelled struck through. */}
                  {pairs(spans).map(([{ stop, to: seam }, { stop: next, from: arrives }]) => {
                    const how = stop.mode ? t(`mode.${stop.mode}`) : t('plan.wayOpen');
                    const said = `${how}: ${stop.city} → ${next.city}`;
                    const gone = stop.mode !== null && stop.cancelled.includes(stop.mode);
                    const mark = <ModeIcon mode={stop.mode ?? 'travel'} size={12} />;
                    // Long enough to draw, as this trip's own: its ends, their clocks, how long.
                    const wide = x(arrives) - x(seam);
                    // A ticket of legs: each drawn, the change between them said.
                    const legs = stop.legs.flatMap((leg) =>
                      leg.departureAt && leg.arrivalAt
                        ? [
                            {
                              origin: leg.origin,
                              destination: leg.destination,
                              leaves: Date.parse(leg.departureAt),
                              arrives: Date.parse(leg.arrivalAt),
                              fromZone: leg.originZone,
                              toZone: leg.destinationZone,
                            },
                          ]
                        : [],
                    );
                    // A change on the way is a stop of its own: each leg then its own way.
                    if (
                      !next.transfer &&
                      legs.length > 1 &&
                      legs.length === stop.legs.length &&
                      wide >= 24 * legs.length &&
                      stop.mode !== null
                    ) {
                      return (
                        <Fragment key={`${stop.id}-way`}>
                          {legBars(legs, {
                            x,
                            key: `${stop.id}-way`,
                            mode: stop.mode,
                            title: said,
                            picked: kinWay === stop.id,
                            onPick:
                              onKin &&
                              (() => {
                                onKin(trip.id, stop.id, true);
                              }),
                            className: `tl-kin-trip${gone ? ' tl-kin-way-gone' : ''}`,
                            style: {},
                            end: clockEnd,
                          })}
                        </Fragment>
                      );
                    }
                    if (wide >= 24) {
                      const from = iataOf(stop.ends?.from) ?? stop.city;
                      const to = iataOf(stop.ends?.to) ?? next.city;
                      const leaves = wallOf(new Date(seam).toISOString(), stop.zone).time;
                      const comes = wallOf(new Date(arrives).toISOString(), next.zone).time;
                      const length = minutesSaid(Math.round((arrives - seam) / 60_000));
                      const fits = (letters: number) => letters * LETTER + SPARE <= wide;
                      const ends = fits(from.length + to.length + 2);
                      const timed = ends && fits(from.length + to.length + 14);
                      const whole = timed && fits(from.length + to.length + 17 + length.length);
                      return (
                        <span
                          key={`${stop.id}-way`}
                          className={`tl-trip tl-kin-trip tl-trip-${stop.mode ?? 'travel'}${kinWay === stop.id ? ' tl-selected' : ''}${gone ? ' tl-kin-way-gone' : ''}`}
                          style={{ left: x(seam), width: wide }}
                        >
                          {ends &&
                            clockEnd(
                              { id: `${stop.id}-from`, city: stop.city, zone: stop.zone },
                              from,
                              timed ? leaves : null,
                            )}
                          <button
                            type="button"
                            className="tl-trip-line"
                            aria-label={said}
                            title={said}
                            aria-pressed={kinWay === stop.id}
                            disabled={!onKin}
                            onClick={() => {
                              here.current = kinWay !== stop.id;
                              onKin?.(trip.id, stop.id, true);
                            }}
                          >
                            {mark}
                            {whole && length}
                          </button>
                          {ends &&
                            clockEnd(
                              { id: `${stop.id}-to`, city: next.city, zone: next.zone },
                              to,
                              timed ? comes : null,
                            )}
                        </span>
                      );
                    }
                    const look = `tl-kin-way${gone ? ' tl-kin-way-gone' : ''}${kinWay === stop.id ? ' tl-selected' : ''}`;
                    // Pressed, as this trip's own way is: the stop it leaves picked, shown on the map.
                    return onKin ? (
                      <button
                        key={`${stop.id}-way`}
                        type="button"
                        className={look}
                        aria-label={said}
                        title={said}
                        style={{ left: x(seam) }}
                        aria-pressed={kinWay === stop.id}
                        onClick={() => {
                          here.current = kinWay !== stop.id;
                          onKin(trip.id, stop.id, true);
                        }}
                      >
                        {mark}
                      </button>
                    ) : (
                      <span
                        key={`${stop.id}-way`}
                        className={look}
                        role="img"
                        aria-label={said}
                        title={said}
                        style={{ left: x(seam) }}
                      >
                        {mark}
                      </span>
                    );
                  })}
                </div>
              ))}
            {/* After the trip, time to plan: pressed, a stop is added at its end. */}
            {route.slice(within || locked ? route.length : -1).map((last) => (
              <button
                key="after"
                type="button"
                className="tl-after"
                aria-label={t('plan.addStop')}
                title={t('plan.addStop')}
                style={{
                  left: x(last.to) + 8,
                  right: 0,
                  top: routeTop + 6,
                  height: ROUTE - 12,
                }}
                onClick={() => {
                  onAddAfter(last.stop);
                }}
              >
                +
              </button>
            ))}
            {/* From far, the ways' marks would cover their stops: they are drawn from a week on. */}
            {perDay >= PER_DAY.week &&
              route.flatMap((block, at) => {
                const next = route[at + 1];
                const { out } = block;
                return next && out ? [wayBlock(block, out, next)] : [];
              })}
            {(alone ? [] : holes).map((hole) => {
              const { gap } = hole;
              const name = t('plan.gap', { count: gap.days });
              return (
                <span
                  key={gap.after.id}
                  className="tl-hole"
                  style={{
                    left: x(hole.from) + 1,
                    width: Math.max(16, x(hole.to) - x(hole.from) - 2),
                    top: routeTop + 6,
                    height: ROUTE - 12,
                  }}
                >
                  <Popover
                    open={gapShown === gap.after.id}
                    onOpenChange={(open) => {
                      setGapShown(open ? gap.after.id : null);
                    }}
                    label={name}
                    trigger={(props) => (
                      <button
                        type="button"
                        className="tl-hole-button"
                        {...props}
                        aria-label={name}
                      >
                        {t('timeline.gapShort', { count: gap.days })}
                      </button>
                    )}
                  >
                    <div className="tl-gap">
                      <strong>{name}</strong>
                      <span className="quiet">{dayRange(gap.first, gap.last, lang)}</span>
                      {!locked && (
                        <button
                          type="button"
                          className="button button-small button-primary"
                          onClick={() => {
                            setGapShown(null);
                            onAddAfter(gap.after);
                          }}
                        >
                          {t('plan.gapAdd')}
                        </button>
                      )}
                      {editable(gap.after) && (
                        <button
                          type="button"
                          className="button button-small"
                          onClick={() => {
                            setGapShown(null);
                            changeDays(gap.after, gap.after.days + gap.days);
                          }}
                        >
                          {t('plan.gapLonger', { city: gap.after.city })}
                        </button>
                      )}
                    </div>
                  </Popover>
                </span>
              );
            })}
            {route.map(stopBlock)}
            {/* The sun rising or setting on a flight, where the plane is then: when pointed at. */}
            {sunsOnWay.map((turn) => {
              const said = t(turn.rise ? 'timeline.sunriseOnWay' : 'timeline.sunsetOnWay', {
                time: clockAt(turn.at),
                where: `${String(Math.round(Math.abs(turn.lat)))}°${'SN'.charAt(Number(turn.lat >= 0))} ${String(Math.round(Math.abs(turn.lon)))}°${'WE'.charAt(Number(turn.lon >= 0))}`,
              });
              return (
                <span
                  key={turn.at}
                  className="tl-sun"
                  style={{ left: x(turn.at), top: routeTop + 2 }}
                  role="img"
                  aria-label={said}
                  title={said}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d={turn.rise ? SUNRISE : SUNSET} />
                  </svg>
                </span>
              );
            })}
            {target && drag && (
              <span
                className="tl-drop"
                style={{
                  left: x(target.at < drag.block.at ? target.from : target.to),
                  top: routeTop,
                  height: ROUTE,
                }}
              />
            )}
            <Read
              render={(said) =>
                said && (
                  <span
                    className={`tl-cursor${said.held ? ' tl-cursor-held' : ''}`}
                    style={{ left: x(said.at), height: tall }}
                  >
                    {/* Its moment over the ruler, in the clock picked and where one is. */}
                    <span className="tl-read">
                      {said.local === '' ? said.clock : `${said.clock} · ${said.local}`}
                    </span>
                  </span>
                )
              }
            />
            {spans.map(([from, to]) => (
              <span
                key="aim"
                ref={aimed}
                className="tl-aim"
                style={{ left: x(from), width: x(to) - x(from) }}
              />
            ))}
            {today && (
              <span className="tl-today" style={{ left: x(now) }}>
                <span>{t('timeline.today')}</span>
              </span>
            )}
            {moment && (
              <MomentLine
                moment={moment}
                x={x}
                start={start}
                end={end}
                perDay={perDay}
                said={momentSaid}
              />
            )}
          </div>
        </div>
      </div>
      {/* What was done, said to a screen reader; taken back from the undo on the bar. */}
      {done && (
        <p className="visually-hidden" role="status">
          {done.said}
        </p>
      )}
    </section>
  );
}

/** Playing the trip through on the map, and how fast: drawn again only as they change. */
function PlayControls({ moment }: { moment: Moment }) {
  const { t } = useTranslation();
  const playing = useMomentOf(moment, (now) => now.playing());
  const speed = useMomentOf(moment, (now) => now.speed());
  return (
    <div className="tl-play" role="group" aria-label={t('timeline.play.group')}>
      <button
        type="button"
        className="icon-button icon-button-small"
        aria-label={t(playing ? 'timeline.play.pause' : 'timeline.play.play')}
        title={t(playing ? 'timeline.play.pause' : 'timeline.play.play')}
        onClick={() => {
          if (playing) moment.pause();
          else moment.play();
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
          <path d={playing ? 'M7 5h4v14H7zM13 5h4v14h-4z' : 'M7 5v14l12-7z'} />
        </svg>
      </button>
      <div
        className="segmented segmented-small"
        role="group"
        aria-label={t('timeline.play.speed')}
      >
        {SPEEDS.map((one) => (
          <button
            key={one}
            type="button"
            aria-pressed={speed === one}
            onClick={() => {
              moment.setSpeed(one);
            }}
          >
            {t(`timeline.play.x${String(one)}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The moment the map shows, a red line over the rows: held and drawn along, or moved by the
 * arrows — an hour, a day with Shift. Only it is drawn again as the moment moves.
 */
function MomentLine({
  moment,
  x,
  start,
  end,
  perDay,
  said,
}: {
  moment: Moment;
  x: (at: number) => number;
  start: number;
  end: number;
  perDay: number;
  /** The moment in the clock picked and where one is then: `T 23.12 16:21 · 23:21 Bangkok`. */
  said: (at: number) => string;
}) {
  const { t } = useTranslation();
  const at = useMomentOf(moment, (now) => now.at());
  // Where it was taken hold of: the pointer then and the moment then. Drawn along from there, the
  // moment is where the pointer is — not added up from where the line was last drawn, which the
  // page may not have moved yet between two moves of the pointer.
  const [held, setHeld] = useState<{ x: number; at: number } | null>(null);
  return (
    <span
      className={`tl-moment${held === null ? '' : ' tl-moment-held'}`}
      style={{ left: x(at) }}
    >
      <span
        className="tl-moment-grip"
        role="slider"
        tabIndex={0}
        aria-label={t('timeline.play.moment')}
        aria-valuemin={start}
        aria-valuemax={end}
        aria-valuenow={Math.round(at)}
        aria-valuetext={said(at)}
        onPointerDown={(event) => {
          event.stopPropagation();
          event.currentTarget.setPointerCapture(event.pointerId);
          moment.pause();
          setHeld({ x: event.clientX, at: moment.at() });
        }}
        onPointerMove={(event) => {
          if (held === null || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
          moment.set(held.at + ((event.clientX - held.x) / perDay) * DAY);
        }}
        onLostPointerCapture={() => {
          setHeld(null);
        }}
        onKeyDown={(event) => {
          const by = event.shiftKey ? DAY : DAY / 24;
          if (event.key === 'ArrowLeft') moment.set(moment.at() - by);
          else if (event.key === 'ArrowRight') moment.set(moment.at() + by);
          else return;
          event.preventDefault();
        }}
      />
      {/* Its moment always said beside its handle, as the line under the pointer says its own. */}
      <span className="tl-moment-read" aria-hidden="true">
        {said(at)}
      </span>
    </span>
  );
}
